import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

type ItemChange = RealtimePostgresChangesPayload<{
  [key: string]: unknown;
}>;

interface UseRealtimeItemsOptions {
  table?: string;
  filter?: string;
  onInsert?: (payload: ItemChange) => void;
  onUpdate?: (payload: ItemChange) => void;
  onDelete?: (payload: ItemChange) => void;
  enabled?: boolean;
  channelName?: string;
}

/**
 * Hook to subscribe to real-time changes on a Supabase table
 * Automatically handles subscription cleanup and connection management
 *
 * @example
 * ```tsx
 * useRealtimeItems({
 * filter: 'status=eq.approved',
 * onInsert: () => mutate(),
 * onUpdate: () => mutate(),
 * onDelete: () => mutate(),
 * });
 * ```
 */
export function useRealtimeItems({
  table = "items",
  filter,
  onInsert,
  onUpdate,
  onDelete,
  enabled = true,
  channelName,
}: UseRealtimeItemsOptions) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const callbacksRef = useRef({ onInsert, onUpdate, onDelete });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onInsert, onUpdate, onDelete };
  }, [onInsert, onUpdate, onDelete]);

  // Memoized change handler that uses the latest callbacks
  const handleChange = useCallback((payload: ItemChange) => {
    const { onInsert, onUpdate, onDelete } = callbacksRef.current;

    if (payload.eventType === "INSERT" && onInsert) {
      onInsert(payload);
    } else if (payload.eventType === "UPDATE" && onUpdate) {
      onUpdate(payload);
    } else if (payload.eventType === "DELETE" && onDelete) {
      onDelete(payload);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Clean up existing subscription if disabled
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch (_error) {
          // Ignore cleanup errors
        }
        channelRef.current = null;
      }
      return;
    }

    // Generate unique channel name if not provided
    const uniqueChannelName =
      channelName ||
      `realtime-${table}-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    let channel: ReturnType<typeof supabase.channel>;
    let subscription: ReturnType<typeof channel.subscribe>;

    try {
      channel = supabase.channel(uniqueChannelName, {
        config: {
          broadcast: { self: false },
          presence: { key: "" },
        },
      });

      // Build postgres_changes config - only include filter if it's defined
      const postgresConfig: {
        event: "*";
        schema: "public";
        table: string;
        filter?: string;
      } = {
        event: "*",
        schema: "public",
        table,
      };

      if (filter) {
        postgresConfig.filter = filter;
      }

      subscription = channel
        .on("postgres_changes", postgresConfig, handleChange)
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            console.log(
              `[Realtime] Subscribed to ${table} changes${filter ? ` with filter: ${filter}` : ""}`,
            );
          } else if (status === "CHANNEL_ERROR") {
            console.error(
              `[Realtime] Error subscribing to ${table} changes. ` +
                `Possible causes: Realtime not enabled, table not in publication, or RLS blocking access. ` +
                `Check Supabase Dashboard → Database → Replication.`,
            );
          } else if (status === "TIMED_OUT") {
            console.warn(
              `[Realtime] Subscription to ${table} timed out. Retrying...`,
            );
          } else if (status === "CLOSED") {
            console.warn(`[Realtime] Subscription to ${table} closed`);
          } else {
            console.log(`[Realtime] Subscription status for ${table}:`, status);
          }
        });

      channelRef.current = channel;
    } catch (error) {
      console.error(
        `[Realtime] Failed to create subscription for ${table}:`,
        error,
      );
      return;
    }

    return () => {
      try {
        if (subscription) {
          subscription.unsubscribe();
        }
        if (channel) {
          supabase.removeChannel(channel);
        }
      } catch (_error) {
        // Ignore cleanup errors during unmount
      } finally {
        channelRef.current = null;
      }
    };
  }, [table, filter, enabled, channelName, handleChange]);
}
